import {
  PostgrestClient,
  type PostgrestMaybeSingleResponse,
  type PostgrestResponse,
  type PostgrestSingleResponse,
} from "@supabase/postgrest-js";
import {
  type Database,
  type DatabaseWithOptions,
} from "../../../__definitions__/database-definitions";
import {
  SupabaseService,
  SupabaseServiceView,
} from "../../../server/supabase/base-supabase-service";
import type { UUID } from "../../../utils/uuid";
import type { ChannelName } from "../../channels/types";
import type { EditableCustomDashboardConfig } from "../../luzmo/custom-dashboard-config";

export type Account = Database["public"]["Tables"]["account"]["Row"];
export type AccountLimit = Database["public"]["Tables"]["account_limit"]["Row"];

export type AdminAccountLimit = Database["ce_admin"]["Views"]["account_limits"]["Row"];
export type AdminCustomLuzmoDashboard =
  Database["ce_admin"]["Views"]["custom_luzmo_dashboards"]["Row"];
export type CustomLuzmoDashboard = Database["public"]["Tables"]["custom_luzmo_dashboard"]["Row"];
export type AdminCustomContentBrand =
  Database["ce_admin"]["Views"]["account_custom_content_brand"]["Row"];
export type AdminSlackApp = Database["ce_admin"]["Views"]["slack_apps"]["Row"];

const LISTEN_ENTITLEMENT = ["Disabled", "Basic", "Pro"] as const;
export type ListenEntitlement = (typeof LISTEN_ENTITLEMENT)[number];

export function listenEnabled(entitlement: ListenEntitlement | null): boolean {
  return entitlement === "Basic" || entitlement === "Pro";
}

export const DEFAULT_SES_CONFIGURATION_SET = "email-engine";
export const NO_TRACKING_SES_CONFIGURATION_SET = "email-engine-no-pixel-tracking";

export const ACCOUNT_LIMIT_KEYS = {
  slack_aliases: { type: "number" },
  super_admins: { type: "number" },
  contributors: { type: "number" },
  unlayer_fallback: { type: "number" },
  omit_email_test_indicator: { type: "number" },
  email_design_templates: { type: "number" },
  allow_all_and_upcoming_segment: { type: "boolean" },
  slack_connections: { type: "number" },
  enable_sms_two_way: { type: "boolean" },
  enable_swag: { type: "boolean" },
  enable_e_gifting: { type: "boolean" },
  enable_videography: { type: "boolean" },
  enable_design_services: { type: "boolean" },
  enable_listen: { type: "boolean" },
  enable_survey_ai_analysis: { type: "boolean" },
  design_services: { type: "number" },
  distribution_credit: { type: "number" },
  listen_entitlement: { type: "select", values: LISTEN_ENTITLEMENT },
  enable_polls: { type: "boolean" },
  enable_knowledge_hub: { type: "boolean" },
  hide_additional_services_upsell: { type: "boolean" },
  enable_pii_guidance: { type: "boolean" },
  communication_insights: { type: "boolean" },
  enable_advanced_email_headers: { type: "boolean" },
  enable_cc_bcc: { type: "boolean" },
} as const;

export type AccountLimitKey = keyof typeof ACCOUNT_LIMIT_KEYS;

export class SupabaseAccountService extends SupabaseService<"account"> {
  table = "account" as const;

  async getEnabledChannels(account_id: UUID): Promise<NonNullable<ChannelName>[]> {
    const { data } = await this.client
      .from(this.table)
      .select("enable_slack,enable_teams,enable_twilio,enable_whatsapp")
      .eq("id", account_id)
      .single();

    return [
      ...(data && data.enable_slack ? ["slack" as const] : []),
      "work_email" as const,
      "personal_email" as const,
      ...(data && data.enable_teams ? ["teams" as const] : []),
      ...(data && data.enable_twilio ? ["twilio" as const] : []),
      ...(data && data.enable_whatsapp ? ["whatsapp" as const] : []),
    ];
  }
}

export class SupabaseAccountEmployeeSyncConfigService extends SupabaseService<"account_employee_sync_config"> {
  table = "account_employee_sync_config" as const;
}

export class SupabaseAccountLimitService extends SupabaseService<"account_limit"> {
  table = "account_limit" as const;

  async getKey(key: AccountLimitKey): Promise<PostgrestMaybeSingleResponse<AccountLimit>> {
    return this.log_errors(
      await this.client.from(this.table).select<"*", AccountLimit>().eq("key", key).maybeSingle(),
    );
  }

  async getKeys(keys: AccountLimitKey[]): Promise<PostgrestResponse<AccountLimit>> {
    return this.log_errors(
      await this.client.from(this.table).select<"*", AccountLimit>().in("key", keys),
    );
  }
}

export class SupabaseCustomLuzmoDashboardService extends SupabaseService<"custom_luzmo_dashboard"> {
  table = "custom_luzmo_dashboard" as const;

  async getCustomLuzmoDashboards(
    accountId: UUID,
  ): Promise<PostgrestResponse<CustomLuzmoDashboard>> {
    return this.client
      .from(this.table)
      .select<"*", CustomLuzmoDashboard>("*")
      .eq("account_id", accountId)
      .order("created_at")
      .order("id");
  }
}

export class SupabaseDesignHuddleCustomBrandService extends SupabaseService<"content_custom_template_brand"> {
  table = "content_custom_template_brand" as const;
}

export class CeAdminSupabaseAccountService {
  client: PostgrestClient<Database, DatabaseWithOptions["options"], "ce_admin">;

  constructor(client: PostgrestClient<Database, DatabaseWithOptions["options"], "ce_admin">) {
    this.client = client;
  }

  async getAccountLimits(): Promise<PostgrestResponse<AdminAccountLimit>> {
    return await this.client.from("account_limits").select<"*", AdminAccountLimit>();
  }

  async getAccountLimit(account_id: UUID): Promise<PostgrestSingleResponse<AdminAccountLimit>> {
    return await this.client
      .from("account_limits")
      .select<"*", AdminAccountLimit>("*")
      .eq("id", account_id)
      .single();
  }

  async getCustomLuzmoDashboards(
    accountId: UUID,
  ): Promise<PostgrestResponse<AdminCustomLuzmoDashboard>> {
    return this.client
      .from("custom_luzmo_dashboards")
      .select<"*", AdminCustomLuzmoDashboard>("*")
      .eq("account_id", accountId)
      .order("created_at")
      .order("id");
  }

  async upsertCustomLuzmoDashboard(
    accountId: UUID,
    config: EditableCustomDashboardConfig,
  ): Promise<PostgrestSingleResponse<UUID>> {
    return this.client.rpc("upsert_custom_luzmo_dashboard", {
      account_id_: accountId,
      // The generated PostgREST type does not capture that this RPC uses null
      // to distinguish an insert from an update.
      dashboard_id_: config.id as UUID,
      enabled_: config.enabled,
      url_: config.url,
      tab_name_: config.tabName,
    });
  }

  async deleteCustomLuzmoDashboard(
    accountId: UUID,
    dashboardId: UUID,
  ): Promise<PostgrestSingleResponse<undefined>> {
    return this.client.rpc("delete_custom_luzmo_dashboard", {
      account_id_: accountId,
      dashboard_id_: dashboardId,
    });
  }

  async getCustomBrand(
    account_id: UUID,
  ): Promise<PostgrestMaybeSingleResponse<AdminCustomContentBrand>> {
    return await this.client
      .from("account_custom_content_brand")
      .select<"*", AdminCustomContentBrand>("*")
      .eq("id", account_id)
      .maybeSingle();
  }

  async setCustomBrand(
    account_id: UUID,
    brand_id: number | undefined,
  ): Promise<PostgrestSingleResponse<undefined>> {
    return this.client.rpc("upsert_account_custom_content_brand", {
      account_id_: account_id,
      brand_id_: brand_id,
    });
  }

  async getSlackApps(account_id: UUID): Promise<PostgrestResponse<AdminSlackApp>> {
    return await this.client
      .from("slack_apps")
      .select<"*", AdminSlackApp>("*")
      .eq("account_id", account_id)
      .order("created_at", { ascending: false });
  }

  async setAccountLimits(data: {
    account_id: UUID;
    limits: { [key: string]: number };
    business_name: string;
    usage: "DEMO" | "TESTING" | "CLIENT";
    churned: boolean;
    enable_slack: boolean;
    enable_teams: boolean;
    enable_twilio: boolean;
    enable_whatsapp: boolean;
    enable_work_email: boolean;
    enable_personal_email: boolean;
    ses_configuration_set: string;
  }): Promise<PostgrestSingleResponse<undefined>> {
    return this.client.rpc("upsert_account_limits", data);
  }

  async createAccount(
    data: Database["ce_admin"]["Functions"]["create_account"]["Args"],
  ): Promise<PostgrestSingleResponse<string>> {
    return this.client.rpc("create_account", data);
  }
}

export type UpsellServices = Database["public"]["Views"]["upsell_services"]["Row"];
export class SupabaseUpsellServices extends SupabaseServiceView<"upsell_services"> {
  table = "upsell_services" as const;
}
